import { useEffect, useState, type FormEvent } from "react";
import {
  DEFAULT_SMS_COUNTRY_CODE,
  type SmsBudgetPayload,
  type SmsLedgerEntry,
  type SmsSettingsPublic,
} from "@tih/shared";
import {
  ApiError,
  adminFetchSettingsSms,
  adminFetchSmsBudget,
  adminPutSettingsSms,
  adminTestSettingsSms,
  adminTopUpSmsBudget,
} from "../../../api/client";

const EMPTY: SmsSettingsPublic = {
  username: "",
  senderId: "",
  defaultCountryCode: DEFAULT_SMS_COUNTRY_CODE,
  sandbox: false,
  apiKeySet: false,
  configured: false,
  encryptionAvailable: false,
};

const EMPTY_BUDGET: SmsBudgetPayload = {
  budget: { balanceUgx: 0, costPerSmsUgx: 25, updatedAt: new Date(0).toISOString() },
  ledger: [],
};

function formatUgx(n: number): string {
  return `UGX ${n.toLocaleString()}`;
}

function ledgerKindLabel(kind: SmsLedgerEntry["kind"]): string {
  if (kind === "TOP_UP") return "Top-up";
  if (kind === "SEND_DEBIT") return "Send debit";
  return "Adjust";
}

export function AdminSettingsSmsPage() {
  const [form, setForm] = useState(EMPTY);
  const [apiKey, setApiKey] = useState("");
  const [testTo, setTestTo] = useState("");
  const [budgetPayload, setBudgetPayload] = useState<SmsBudgetPayload>(EMPTY_BUDGET);
  const [topUpAmount, setTopUpAmount] = useState("");
  const [topUpNote, setTopUpNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [data, budget] = await Promise.all([
        adminFetchSettingsSms(),
        adminFetchSmsBudget().catch(() => EMPTY_BUDGET),
      ]);
      setForm(data);
      setBudgetPayload(budget);
      setApiKey("");
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await adminPutSettingsSms({
        username: form.username.trim(),
        senderId: form.senderId.trim(),
        defaultCountryCode: form.defaultCountryCode.trim() || DEFAULT_SMS_COUNTRY_CODE,
        sandbox: form.sandbox,
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
      });
      setForm(data);
      setApiKey("");
      setSuccess("SMS settings saved.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save SMS settings");
    } finally {
      setBusy(false);
    }
  }

  async function sendTest() {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await adminTestSettingsSms({ to: testTo.trim() });
      const cost =
        res.costUgx != null ? ` Debited ${formatUgx(res.costUgx)} from SMS budget.` : "";
      setSuccess(`Test SMS sent to ${res.sentTo}.${cost}`);
      const budget = await adminFetchSmsBudget().catch(() => null);
      if (budget) setBudgetPayload(budget);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send test SMS");
    } finally {
      setBusy(false);
    }
  }

  async function topUp(e: FormEvent) {
    e.preventDefault();
    const amountUgx = Math.floor(Number(topUpAmount));
    if (!Number.isFinite(amountUgx) || amountUgx <= 0) {
      setError("Enter a positive top-up amount in UGX.");
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await adminTopUpSmsBudget({
        amountUgx,
        ...(topUpNote.trim() ? { note: topUpNote.trim() } : {}),
      });
      setBudgetPayload(data);
      setTopUpAmount("");
      setTopUpNote("");
      setSuccess(`Recorded SMS credit of ${formatUgx(amountUgx)}. New balance ${formatUgx(data.budget.balanceUgx)}.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not record SMS top-up");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <div style={{ opacity: 0.5 }}>Loading…</div>;
  }

  const { budget, ledger } = budgetPayload;
  const suggestedWithFee = (() => {
    const n = Math.floor(Number(topUpAmount));
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.ceil(n * 1.02);
  })();

  return (
    <div>
      <section className="admin-card">
        <h2 className="admin-card-title">SMS budget</h2>
        <p className="admin-card-sub">
          Each successful recipient send costs{" "}
          <strong>{formatUgx(budget.costPerSmsUgx)}</strong> (default UGX 25). Sends are
          rejected when the balance cannot cover the whole campaign. Sandbox sends are
          debited too so accounting stays realistic.
        </p>
        <p style={{ fontSize: "1.25rem", margin: "12px 0" }}>
          Balance: <strong>{formatUgx(budget.balanceUgx)}</strong>
          <span className="admin-muted" style={{ marginLeft: 12, fontSize: "0.9rem" }}>
            Rate {formatUgx(budget.costPerSmsUgx)} / successful send
          </span>
        </p>

        <div
          style={{
            display: "grid",
            gap: 24,
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.2fr)",
          }}
          className="admin-sms-budget-grid"
        >
          <form onSubmit={(e) => void topUp(e)} className="admin-blog-form">
            <h3 className="admin-card-title" style={{ fontSize: "1.05rem" }}>
              Record credit in this app
            </h3>
            <p className="admin-card-sub">
              After you pay Africa&apos;s Talking (or your provider), enter the credit here.
              Provider payments do not update this balance automatically.
            </p>
            <div className="admin-blog-form-fields">
              <label>
                <span>Amount (UGX)</span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={topUpAmount}
                  disabled={busy}
                  onChange={(e) => setTopUpAmount(e.target.value)}
                  placeholder="50000"
                  required
                />
              </label>
              <label>
                <span>Note (optional)</span>
                <input
                  value={topUpNote}
                  disabled={busy}
                  onChange={(e) => setTopUpNote(e.target.value)}
                  placeholder="Airtel Money ref / receipt"
                  maxLength={400}
                />
              </label>
            </div>
            <div className="admin-blog-form-actions">
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? "Saving…" : "Record top-up"}
              </button>
            </div>
          </form>

          <div>
            <h3 className="admin-card-title" style={{ fontSize: "1.05rem" }}>
              Billing – Top Up (Uganda Airtel Money)
            </h3>
            <p className="admin-card-sub">
              You can top up Africa&apos;s Talking using the method below. Online / mobile-money
              methods credit the <em>provider</em> account automatically when used with their
              billing page — then record the same amount here so our SMS budget matches.
            </p>
            <ul className="admin-muted" style={{ margin: "8px 0 12px", paddingLeft: 18 }}>
              <li>
                <strong>Country:</strong> Uganda
              </li>
              <li>
                <strong>Method:</strong> Airtel Money
              </li>
              <li>
                Airtel Money charges a <strong>2% transaction fee</strong> — add about 2% to
                the amount you dial so the provider receives the full credit you want
                {suggestedWithFee != null
                  ? ` (e.g. dial ~${formatUgx(suggestedWithFee)} to net ~${formatUgx(
                      Math.floor(Number(topUpAmount)),
                    )})`
                  : ""}
                .
              </li>
            </ul>
            <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.55 }}>
              <li>Dial <code>*185#</code></li>
              <li>Select <strong>Paybill</strong></li>
              <li>Select <strong>Others</strong></li>
              <li>
                Enter Business Number <strong>550619</strong>
              </li>
              <li>Enter Amount</li>
              <li>
                Enter Top Up Reference (can be the name of the service — e.g.{" "}
                <em>Together In Heaven SMS</em>)
              </li>
              <li>Confirm Top Up</li>
              <li>Enter MM PIN</li>
            </ol>
          </div>
        </div>

        <h3 className="admin-card-title" style={{ fontSize: "1.05rem", marginTop: 28 }}>
          Recent ledger
        </h3>
        {ledger.length === 0 ? (
          <p className="admin-muted">No top-ups or send debits yet.</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Kind</th>
                  <th>Amount</th>
                  <th>Balance after</th>
                  <th>Note</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((row) => (
                  <tr key={row.id}>
                    <td>{new Date(row.createdAt).toLocaleString()}</td>
                    <td>{ledgerKindLabel(row.kind)}</td>
                    <td>
                      {row.amountUgx > 0 ? "+" : ""}
                      {formatUgx(row.amountUgx)}
                    </td>
                    <td>{formatUgx(row.balanceAfter)}</td>
                    <td style={{ maxWidth: 280 }}>{row.note ?? "—"}</td>
                    <td>{row.createdByName ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="admin-card" style={{ marginTop: 24 }}>
        <h2 className="admin-card-title">Africa&apos;s Talking SMS</h2>
        <p className="admin-card-sub">
          Send texts to attendees and reservations that have mobile numbers.
          Create an app at{" "}
          <a href="https://account.africastalking.com" target="_blank" rel="noreferrer">
            account.africastalking.com
          </a>
          . The API key is encrypted in the database; leave it blank when saving to keep the
          current key.
        </p>

        <form onSubmit={(e) => void save(e)} className="admin-blog-form">
          <div className="admin-blog-form-fields">
            <label>
              <span>Username</span>
              <input
                value={form.username}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                placeholder="togetherinheaven"
                required
              />
            </label>
            <label>
              <span>
                API key{" "}
                {form.apiKeySet ? (
                  <em style={{ fontWeight: 400, opacity: 0.7 }}>(leave blank to keep current)</em>
                ) : (
                  <em style={{ fontWeight: 400, opacity: 0.7 }}>(required on first save)</em>
                )}
              </span>
              <input
                type="password"
                value={apiKey}
                disabled={busy}
                onChange={(e) => setApiKey(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            <label>
              <span>Sender ID / shortcode</span>
              <input
                value={form.senderId}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, senderId: e.target.value }))}
                placeholder="Optional — must be approved by Africa's Talking"
                maxLength={20}
              />
            </label>
            <label>
              <span>Default country code</span>
              <input
                value={form.defaultCountryCode}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, defaultCountryCode: e.target.value }))}
                placeholder="256"
                required
              />
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={form.sandbox}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, sandbox: e.target.checked }))}
              />
              <span>
                Use sandbox (username is usually <code>sandbox</code>)
              </span>
            </label>
          </div>
          <div className="admin-blog-form-actions">
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? "Saving…" : "Save SMS settings"}
            </button>
          </div>
        </form>
      </section>

      <section className="admin-card" style={{ marginTop: 24 }}>
        <h2 className="admin-card-title">Send a test SMS</h2>
        <p className="admin-card-sub">
          Uses the saved credentials and debits the SMS budget on success. Local numbers like
          07… are sent as +{form.defaultCountryCode || DEFAULT_SMS_COUNTRY_CODE}….
        </p>
        <div className="admin-blog-form">
          <div className="admin-blog-form-fields">
            <label>
              <span>Mobile number</span>
              <input
                value={testTo}
                disabled={busy}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="0772 000 000 or +256772000000"
              />
            </label>
          </div>
          <div className="admin-blog-form-actions">
            <button
              type="button"
              className="btn btn-ghost"
              disabled={
                busy ||
                !form.configured ||
                !testTo.trim() ||
                budget.balanceUgx < budget.costPerSmsUgx
              }
              onClick={() => void sendTest()}
            >
              {busy ? "Sending…" : "Send test SMS"}
            </button>
          </div>
        </div>
      </section>

      {error && <div className="form-error">{error}</div>}
      {success && (
        <div className="form-success" style={{ padding: 12 }}>
          {success}
        </div>
      )}
    </div>
  );
}
